import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Home, Plus, Trash2, Edit2, Package, ShoppingCart, 
  Truck, CheckCircle, X, Save, AlertTriangle, Clock,
  FileText, History, Receipt, CreditCard, BanknoteIcon, Archive, Printer,
  Bell, Mail, MessageSquare, Send, Settings, MessageCircle, XCircle, ChevronDown, Star
} from 'lucide-react';
import { printOrderTicketViaQz } from '@/utils/qzTrayService';
import ReviewRequestModal from '@/components/ReviewRequestModal';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Configuration par défaut de l'imprimante
const DEFAULT_PRINTER_CONFIG = {
  useQzTray: true,
  printerName: '',
  ticketWidth: 58,
  shopName: 'GESTION GLOBALE',
  address: '',
  phone: ''
};

export default function OrderAlertsPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [filter, setFilter] = useState('all'); // all, pending, ordered, received, delivered
  const [showArchives, setShowArchives] = useState(false); // Toggle pour afficher les archives
  const [showNotifyModal, setShowNotifyModal] = useState(null); // Pour le modal de notification
  const [notifyChannels, setNotifyChannels] = useState({ email: false, sms: false, whatsapp: false });
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyPhone, setNotifyPhone] = useState('');
  const [sendingNotification, setSendingNotification] = useState(false);
  const [showStatusSubmenu, setShowStatusSubmenu] = useState(null); // Pour le sous-menu de statuts
  const [showReviewModal, setShowReviewModal] = useState(null); // Modal demande d'avis
  
  // Configuration imprimante - utilise la même config que le ticket de caisse
  const [printerConfig] = useState(() => {
    const saved = localStorage.getItem('printerConfig');
    return saved ? JSON.parse(saved) : DEFAULT_PRINTER_CONFIG;
  });
  
  const [formData, setFormData] = useState({
    productName: '',
    quantity: 1,
    destination: '',
    clientName: '',
    clientPhone: '',
    notes: '',
    priority: 'normal' // normal, urgent
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/api/order-alerts`, {
        params: { limit: 100000 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.items || response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.productName || !formData.destination) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    try {
      if (editingOrder) {
        await axios.put(`${API}/api/order-alerts/${editingOrder.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Commande modifiée');
      } else {
        await axios.post(`${API}/api/order-alerts`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Commande ajoutée');
      }

      setShowModal(false);
      resetForm();
      fetchOrders();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/api/order-alerts/${id}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Statut mis à jour: ${getStatusLabel(newStatus)}`);
      fetchOrders();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer cette commande ?')) return;
    
    try {
      await axios.delete(`${API}/api/order-alerts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Commande supprimée');
      fetchOrders();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const togglePayment = async (id) => {
    try {
      const response = await axios.patch(`${API}/api/order-alerts/${id}/payment`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.isPaid ? 'Marqué comme payé' : 'Marqué comme non payé');
      fetchOrders();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Ouvrir le modal de notification
  const openNotifyModal = (order) => {
    setShowNotifyModal(order);
    setNotifyEmail('');
    setNotifyPhone(order.clientPhone || '');
    // Aucun canal pré-sélectionné par défaut (SMS facturés - envoi manuel)
    setNotifyChannels({ email: false, sms: false, whatsapp: false });
  };

  // Envoyer la notification
  const sendNotification = async () => {
    const order = showNotifyModal;
    if (!order) return;
    
    const channels = [];
    if (notifyChannels.email) channels.push('email');
    if (notifyChannels.sms) channels.push('sms');
    if (notifyChannels.whatsapp) channels.push('whatsapp');

    if (channels.length === 0) {
      toast.error('Sélectionnez au moins un canal (Email, SMS ou WhatsApp)');
      return;
    }

    if (notifyChannels.email && !notifyEmail) {
      toast.error('Veuillez saisir l\'email');
      return;
    }

    if ((notifyChannels.sms || notifyChannels.whatsapp) && !notifyPhone) {
      toast.error('Veuillez saisir le téléphone');
      return;
    }

    setSendingNotification(true);
    // Fermer le modal immédiatement pour une meilleure UX
    const orderToNotify = { ...order };
    const emailToSend = notifyEmail;
    const phoneToSend = notifyPhone;
    const channelsToSend = [...channels];
    
    setShowNotifyModal(null);
    setNotifyEmail('');
    setNotifyPhone('');
    setNotifyChannels({ email: false, sms: false, whatsapp: false });
    
    toast.loading('Envoi en cours...', { id: 'notification-sending' });
    
    try {
      const response = await axios.post(
        `${API}/api/order-alerts/${orderToNotify.id}/notify`,
        {
          channels: channelsToSend,
          client_email: emailToSend,
          client_phone: phoneToSend
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000 // 30 secondes max
        }
      );

      toast.dismiss('notification-sending');
      
      if (response.data.success) {
        const results = response.data.results;
        if (results.email?.success) {
          toast.success(`Email envoyé à ${results.email.sent_to}`);
        } else if (results.email?.error) {
          toast.error(`Erreur email: ${results.email.error}`);
        }
        if (results.sms?.success) {
          toast.success(`SMS envoyé à ${results.sms.sent_to}`);
        } else if (results.sms?.error) {
          toast.error(`Erreur SMS: ${results.sms.error}`);
        }
        // Rafraîchir la liste pour mettre à jour le statut "Notifié"
        fetchOrders();
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.dismiss('notification-sending');
      console.error('Notification error:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('Délai dépassé - vérifiez si la notification a été envoyée');
      } else {
        toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
      }
    } finally {
      setSendingNotification(false);
    }
  };

  // Ouvrir le modal de demande d'avis
  const openReviewModal = (order) => {
    setShowReviewModal({
      id: order.id,
      name: order.clientName,
      phone: order.clientPhone,
      email: order.clientEmail,
      source: 'order',
      source_id: order.id,
      purchase_type: `commande de ${order.productName || 'article'}`,
      date: order.createdAt?.slice(0, 10) || ''
    });
  };

  // Impression du ticket de commande via QZ Tray
  const printOrderTicket = async (order) => {
    toast.loading('Connexion à l\'imprimante POS...', { id: 'print-order' });
    
    const result = await printOrderTicketViaQz(order, printerConfig, printerConfig.printerName);
    
    if (result.success) {
      toast.success(`✅ ${result.message}`, { id: 'print-order' });
    } else {
      toast.warning(`⚠️ ${result.message}`, { id: 'print-order' });
      // Fallback: ouvrir dans une nouvelle fenêtre
      printOrderFallback(order);
    }
  };

  // Fallback impression via navigateur
  const printOrderFallback = (order) => {
    const cfg = printerConfig;
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bon de Commande</title>
        <style>
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            width: ${cfg.ticketWidth === 80 ? '72mm' : '48mm'};
            margin: 0 auto;
            padding: 5mm;
          }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .big { font-size: 16px; font-weight: bold; }
          .separator { border-bottom: 1px dashed #000; margin: 5px 0; }
          .row { display: flex; justify-content: space-between; margin: 3px 0; }
          .urgent { border: 2px solid #000; padding: 5px; margin: 5px 0; font-weight: bold; }
          .quantity { font-size: 24px; font-weight: bold; }
        </style>
      </head>
      <body>
        <div class="center bold">${cfg.shopName || 'GESTION GLOBALE'}</div>
        <div class="separator"></div>
        <div class="center big">BON DE COMMANDE</div>
        <div class="center">${new Date(order.createdAt).toLocaleDateString('fr-FR')}</div>
        ${order.priority === 'urgent' ? '<div class="center urgent">⚠️ URGENT ⚠️</div>' : ''}
        <div class="separator"></div>
        <div class="bold">PRODUIT:</div>
        <div class="big">${order.productName}</div>
        ${order.notes ? `<div>${order.notes}</div>` : ''}
        <div class="separator"></div>
        <div class="center">
          <div>Quantité</div>
          <div class="quantity">${order.quantity}</div>
        </div>
        <div class="separator"></div>
        <div class="bold">DESTINATION:</div>
        <div>${order.destination}</div>
        ${order.clientName ? `
          <div class="separator"></div>
          <div class="bold">CLIENT:</div>
          <div>${order.clientName}</div>
          ${order.clientPhone ? `<div>Tel: ${order.clientPhone}</div>` : ''}
        ` : ''}
        <div class="separator"></div>
        ${order.isPaid ? '<div class="center bold" style="color: green; font-size: 14px;">✓ PAYÉ</div>' : ''}
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const resetForm = () => {
    setFormData({
      productName: '',
      quantity: 1,
      destination: '',
      clientName: '',
      clientPhone: '',
      notes: '',
      priority: 'normal'
    });
    setEditingOrder(null);
  };

  const openEditModal = (order) => {
    setEditingOrder(order);
    setFormData({
      productName: order.productName || '',
      quantity: order.quantity || 1,
      destination: order.destination || '',
      clientName: order.clientName || '',
      clientPhone: order.clientPhone || '',
      notes: order.notes || '',
      priority: order.priority || 'normal'
    });
    setShowModal(true);
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'À commander',
      ordered: 'Commandé',
      received: 'Reçu',
      delivered: 'Livré',
      cancelled: 'Annulé',
      unavailable: 'Indisponible'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-red-500',
      ordered: 'bg-amber-500',
      received: 'bg-blue-500',
      delivered: 'bg-emerald-500',
      cancelled: 'bg-gray-500',
      unavailable: 'bg-red-600'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <ShoppingCart className="w-4 h-4" />,
      ordered: <Truck className="w-4 h-4" />,
      received: <Package className="w-4 h-4" />,
      delivered: <CheckCircle className="w-4 h-4" />,
      cancelled: <X className="w-4 h-4" />,
      unavailable: <XCircle className="w-4 h-4" />
    };
    return icons[status] || <Package className="w-4 h-4" />;
  };

  // Séparer les commandes actives des archives (livrées, annulées, indisponibles)
  const activeOrders = orders.filter(o => !['delivered', 'cancelled', 'unavailable'].includes(o.status));
  const archivedOrders = orders.filter(o => ['delivered', 'cancelled', 'unavailable'].includes(o.status));
  
  // Appliquer le filtre sur les commandes affichées
  const displayedOrders = showArchives ? archivedOrders : activeOrders;
  
  const filteredOrders = displayedOrders.filter(o => {
    if (filter === 'all') return true;
    return o.status === filter;
  });

  const pendingCount = activeOrders.filter(o => o.status === 'pending').length;
  const orderedCount = activeOrders.filter(o => o.status === 'ordered').length;
  const receivedCount = activeOrders.filter(o => o.status === 'received').length;

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gray-900 border-b border-gray-700 h-14 flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <Home className="w-4 h-4 mr-2" />
            Caisse
          </Button>
          <Button
            onClick={() => navigate('/invoices')}
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <FileText className="w-4 h-4 mr-2" />
            Facturation
          </Button>
          <Button
            onClick={() => navigate('/deposit-receipts')}
            variant="ghost"
            size="sm"
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <Receipt className="w-4 h-4 mr-2" />
            Bons d'entrée
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-amber-400 bg-white/10"
          >
            <Package className="w-4 h-4 mr-2" />
            Commandes
          </Button>
        </div>
      </header>

      <main className="p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div 
            onClick={() => setFilter('pending')}
            className={`p-4 rounded-xl cursor-pointer transition-all ${filter === 'pending' ? 'ring-2 ring-red-500' : ''} bg-gradient-to-br from-red-600 to-red-700 ${pendingCount > 0 ? 'animate-pulse' : ''}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm">À commander</p>
                <p className="text-3xl font-bold text-white">{pendingCount}</p>
              </div>
              <ShoppingCart className="w-10 h-10 text-white/50" />
            </div>
          </div>
          
          <div 
            onClick={() => setFilter('ordered')}
            className={`p-4 rounded-xl cursor-pointer transition-all ${filter === 'ordered' ? 'ring-2 ring-amber-500' : ''} bg-gradient-to-br from-amber-600 to-amber-700`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm">En attente livraison</p>
                <p className="text-3xl font-bold text-white">{orderedCount}</p>
              </div>
              <Truck className="w-10 h-10 text-white/50" />
            </div>
          </div>
          
          <div 
            onClick={() => setFilter('received')}
            className={`p-4 rounded-xl cursor-pointer transition-all ${filter === 'received' ? 'ring-2 ring-blue-500' : ''} bg-gradient-to-br from-blue-600 to-blue-700 ${receivedCount > 0 ? 'animate-pulse' : ''}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm">Reçu - À livrer</p>
                <p className="text-3xl font-bold text-white">{receivedCount}</p>
              </div>
              <Package className="w-10 h-10 text-white/50" />
            </div>
          </div>
          
          <div 
            onClick={() => setFilter('all')}
            className={`p-4 rounded-xl cursor-pointer transition-all ${filter === 'all' ? 'ring-2 ring-white' : ''} bg-gradient-to-br from-slate-700 to-slate-800`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm">Total commandes</p>
                <p className="text-3xl font-bold text-white">{orders.length}</p>
              </div>
              <Package className="w-10 h-10 text-white/50" />
            </div>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-white">
              {showArchives 
                ? `Archives (${archivedOrders.length} commandes livrées)`
                : filter === 'all' ? `Commandes actives (${activeOrders.length})` : 
                  filter === 'pending' ? 'À commander' :
                  filter === 'ordered' ? 'En cours de livraison' : 
                  filter === 'received' ? 'Reçues - À livrer' : 'Commandes'
              }
            </h2>
            <p className="text-white/50 text-sm">
              {showArchives 
                ? 'Les commandes livrées sont archivées automatiquement' 
                : 'Workflow: À commander → Commandé → Reçu → Livré (archivé)'
              }
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate('/settings')}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              <Settings className="w-4 h-4 mr-2" />
              Paramètres
            </Button>
            <Button
              onClick={() => { setShowArchives(!showArchives); setFilter('all'); }}
              variant={showArchives ? "default" : "outline"}
              className={showArchives 
                ? "bg-slate-600 hover:bg-slate-500" 
                : "border-white/20 text-white hover:bg-white/10"
              }
            >
              <Archive className="w-4 h-4 mr-2" />
              Archives ({archivedOrders.length})
            </Button>
            <Button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="bg-amber-500 hover:bg-amber-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle commande
            </Button>
          </div>
        </div>

        {/* Orders List */}
        <div className="bg-slate-800 rounded-xl border border-white/10 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-700/50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-white/70">Date</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-white/70">Produit</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-white/70">Qté</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-white/70">Destination</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-white/70">Client</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-white/70">Statut</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-white/70">Paiement</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-white/70">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-white/50">
                    Chargement...
                  </td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-white/50">
                    Aucune commande
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr key={order.id} className={`hover:bg-white/5 ${order.priority === 'urgent' ? 'bg-red-900/20' : ''}`}>
                    <td className="px-4 py-3 text-white/70">
                      {new Date(order.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {order.priority === 'urgent' && (
                          <AlertTriangle className="w-4 h-4 text-red-400" />
                        )}
                        <span className="text-white font-medium">{order.productName}</span>
                      </div>
                      {order.notes && (
                        <div className="text-white/50 text-xs mt-1">{order.notes}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white font-bold">{order.quantity}</td>
                    <td className="px-4 py-3 text-white/70">{order.destination}</td>
                    <td className="px-4 py-3">
                      {order.clientName ? (
                        <div>
                          <div className="text-white text-sm">{order.clientName}</div>
                          <div className="text-white/50 text-xs">{order.clientPhone}</div>
                        </div>
                      ) : (
                        <span className="text-white/30">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(order.status)}`}>
                        {getStatusIcon(order.status)}
                        {getStatusLabel(order.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => togglePayment(order.id)}
                        className={`h-8 px-3 font-bold ${
                          order.isPaid 
                            ? 'bg-emerald-500/30 text-emerald-300 border-2 border-emerald-400 shadow-lg shadow-emerald-500/20' 
                            : 'bg-red-500/20 text-red-400 hover:bg-red-500/30 animate-pulse'
                        }`}
                      >
                        {order.isPaid ? (
                          <>
                            <CreditCard className="w-4 h-4 mr-1" />
                            PAYÉ
                          </>
                        ) : (
                          <>
                            <BanknoteIcon className="w-4 h-4 mr-1" />
                            Non payé
                          </>
                        )}
                      </Button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        {/* Status buttons positifs */}
                        {order.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => updateStatus(order.id, 'ordered')}
                            className="bg-amber-500 hover:bg-amber-600 text-xs h-7 animate-pulse"
                          >
                            <Truck className="w-3 h-3 mr-1" />
                            Commandé
                          </Button>
                        )}
                        {order.status === 'ordered' && (
                          <Button
                            size="sm"
                            onClick={() => updateStatus(order.id, 'received')}
                            className="bg-blue-500 hover:bg-blue-600 text-xs h-7 animate-pulse"
                          >
                            <Package className="w-3 h-3 mr-1" />
                            Reçu
                          </Button>
                        )}
                        {order.status === 'received' && (
                          <Button
                            size="sm"
                            onClick={() => updateStatus(order.id, 'delivered')}
                            className="bg-emerald-500 hover:bg-emerald-600 text-xs h-7 animate-pulse"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Livré
                          </Button>
                        )}
                        
                        {/* Sous-menu pour statuts négatifs */}
                        {(order.status === 'pending' || order.status === 'ordered') && (
                          <div className="relative">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setShowStatusSubmenu(showStatusSubmenu === order.id ? null : order.id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2"
                              title="Autres statuts"
                            >
                              <XCircle className="w-3 h-3 mr-1" />
                              <ChevronDown className="w-3 h-3" />
                            </Button>
                            {showStatusSubmenu === order.id && (
                              <div className="absolute right-0 top-8 z-50 bg-gray-800 border border-gray-600 rounded-lg shadow-xl py-1 min-w-[140px]">
                                <button
                                  onClick={() => { updateStatus(order.id, 'cancelled'); setShowStatusSubmenu(null); }}
                                  className="w-full px-3 py-2 text-left text-sm text-gray-400 hover:bg-gray-500/20 flex items-center gap-2"
                                >
                                  <X className="w-4 h-4" />
                                  Annulé
                                </button>
                                <button
                                  onClick={() => { updateStatus(order.id, 'unavailable'); setShowStatusSubmenu(null); }}
                                  className="w-full px-3 py-2 text-left text-sm text-red-500 hover:bg-red-500/20 flex items-center gap-2"
                                >
                                  <XCircle className="w-4 h-4" />
                                  Indisponible
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => printOrderTicket(order)}
                          className="text-white/70 hover:text-white hover:bg-white/10 h-7 w-7 p-0"
                          title="Imprimer le ticket"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        {/* Bouton Notifier - Change de couleur si déjà notifié */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openNotifyModal(order)}
                          className={`h-7 px-2 ${
                            order.clientNotified 
                              ? 'text-emerald-300 bg-emerald-500/30 hover:bg-emerald-500/40 border border-emerald-500/50' 
                              : 'text-blue-400 hover:text-blue-300 hover:bg-blue-500/10'
                          }`}
                          title={order.clientNotified ? 'Client déjà notifié - Renvoyer' : 'Notifier le client'}
                        >
                          <Bell className="w-3 h-3 mr-1" />
                          <span className="text-xs">{order.clientNotified ? '✓ Notifié' : 'Notifier'}</span>
                        </Button>
                        
                        {/* Bouton Demander Avis - affiché pour les commandes livrées */}
                        {showArchives && order.status === 'delivered' && (order.clientPhone || order.clientEmail) && (
                          <Button
                            size="sm"
                            onClick={() => openReviewModal(order)}
                            className="bg-yellow-500 hover:bg-yellow-600 text-white h-7 px-2 text-xs"
                            title="Demander un avis client"
                            data-testid="quick-review-btn"
                          >
                            <Star className="w-3 h-3 mr-1" />
                            Avis
                          </Button>
                        )}
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditModal(order)}
                          className="text-white/70 hover:text-white hover:bg-white/10 h-7 w-7 p-0"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(order.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 w-7 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* Modal Create/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl shadow-xl max-w-lg w-full border border-white/20">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {editingOrder ? 'Modifier la commande' : 'Nouvelle commande'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-white/50 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-white/70 mb-1">Produit / Article *</label>
                <Input
                  value={formData.productName}
                  onChange={(e) => setFormData({...formData, productName: e.target.value})}
                  placeholder="Ex: Coque Pixel 7, Écran iPhone 14..."
                  className="bg-white/10 border-white/20 text-white"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white/70 mb-1">Quantité</label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 1})}
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/70 mb-1">Priorité</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                    className="w-full h-10 px-3 rounded-md bg-white/10 border border-white/20 text-white"
                  >
                    <option value="normal" className="bg-slate-800">Normal</option>
                    <option value="urgent" className="bg-slate-800">🔴 Urgent</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-white/70 mb-1">Fournisseur / Destination *</label>
                <Input
                  value={formData.destination}
                  onChange={(e) => setFormData({...formData, destination: e.target.value})}
                  placeholder="Ex: Amazon, Fournisseur X, AliExpress..."
                  className="bg-white/10 border-white/20 text-white"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-white/70 mb-1">Nom client (optionnel)</label>
                  <Input
                    value={formData.clientName}
                    onChange={(e) => setFormData({...formData, clientName: e.target.value})}
                    placeholder="Jean Dupont"
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/70 mb-1">Téléphone client</label>
                  <Input
                    value={formData.clientPhone}
                    onChange={(e) => setFormData({...formData, clientPhone: e.target.value})}
                    placeholder="06 12 34 56 78"
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-white/70 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Détails supplémentaires..."
                  rows={2}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-md text-white placeholder:text-white/40"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                >
                  Annuler
                </Button>
                <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600">
                  <Save className="w-4 h-4 mr-2" />
                  {editingOrder ? 'Modifier' : 'Ajouter'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de notification */}
      {showNotifyModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl shadow-xl max-w-md w-full border border-white/20">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Bell className="w-5 h-5 text-blue-400" />
                Notifier le client
              </h2>
              <button onClick={() => setShowNotifyModal(null)} className="text-white/50 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Info de la commande */}
              <div className="bg-white/5 rounded-lg p-3">
                <div className="text-sm text-white/50">Commande</div>
                <div className="text-white font-medium">{showNotifyModal.productName}</div>
                <div className="text-sm text-white/50 mt-2">Client</div>
                <div className="text-white">{showNotifyModal.clientName || 'Non renseigné'}</div>
                <div className="text-sm text-white/50 mt-2">Statut</div>
                <div className={`inline-flex items-center gap-1 text-sm px-2 py-0.5 rounded ${
                  showNotifyModal.status === 'pending' ? 'bg-amber-500/20 text-amber-300' :
                  showNotifyModal.status === 'ordered' ? 'bg-blue-500/20 text-blue-300' :
                  showNotifyModal.status === 'received' ? 'bg-green-500/20 text-green-300' :
                  'bg-emerald-500/20 text-emerald-300'
                }`}>
                  {showNotifyModal.status === 'pending' && 'À commander'}
                  {showNotifyModal.status === 'ordered' && 'Commandé'}
                  {showNotifyModal.status === 'received' && 'Reçu - À livrer'}
                  {showNotifyModal.status === 'delivered' && 'Livré'}
                </div>
              </div>

              {/* Type de notification */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <div className="text-sm text-blue-400 font-medium">
                  {showNotifyModal.status === 'pending' && "📦 Commande en attente"}
                  {showNotifyModal.status === 'ordered' && "🚚 Commande passée"}
                  {showNotifyModal.status === 'received' && "✅ Article disponible !"}
                  {showNotifyModal.status === 'delivered' && "🎉 Commande livrée"}
                </div>
              </div>

              {/* Canaux de notification */}
              <div className="space-y-3">
                <div className="text-sm text-white/70 font-medium">Envoyer par :</div>
                
                <label className="flex items-center gap-3 p-3 rounded-lg border border-white/10 cursor-pointer hover:bg-white/5">
                  <input
                    type="checkbox"
                    checked={notifyChannels.email}
                    onChange={(e) => setNotifyChannels({...notifyChannels, email: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <Mail className="w-5 h-5 text-blue-400" />
                  <div className="flex-1">
                    <div className="text-white text-sm">Email</div>
                    {notifyChannels.email && (
                      <Input
                        type="email"
                        value={notifyEmail}
                        onChange={(e) => setNotifyEmail(e.target.value)}
                        placeholder="client@email.com"
                        className="mt-2 bg-white/10 border-white/20 text-white text-sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </div>
                </label>

                <label className="flex items-center gap-3 p-3 rounded-lg border border-white/10 cursor-pointer hover:bg-white/5">
                  <input
                    type="checkbox"
                    checked={notifyChannels.sms}
                    onChange={(e) => setNotifyChannels({...notifyChannels, sms: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <MessageSquare className="w-5 h-5 text-green-400" />
                  <div className="flex-1">
                    <div className="text-white text-sm">SMS</div>
                    {notifyChannels.sms && (
                      <Input
                        value={notifyPhone}
                        onChange={(e) => setNotifyPhone(e.target.value)}
                        placeholder="06 12 34 56 78"
                        className="mt-2 bg-white/10 border-white/20 text-white text-sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </div>
                </label>

                <label className="flex items-center gap-3 p-3 rounded-lg border border-green-500/30 cursor-pointer hover:bg-green-500/10 bg-green-500/5">
                  <input
                    type="checkbox"
                    checked={notifyChannels.whatsapp}
                    onChange={(e) => setNotifyChannels({...notifyChannels, whatsapp: e.target.checked})}
                    className="w-4 h-4 rounded accent-green-500"
                  />
                  <MessageCircle className="w-5 h-5 text-green-500" />
                  <div className="flex-1">
                    <div className="text-green-400 text-sm font-medium">WhatsApp</div>
                    {notifyChannels.whatsapp && (
                      <Input
                        value={notifyPhone}
                        onChange={(e) => setNotifyPhone(e.target.value)}
                        placeholder="06 12 34 56 78"
                        className="mt-2 bg-white/10 border-green-500/30 text-white text-sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </div>
                </label>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowNotifyModal(null)}
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                  disabled={sendingNotification}
                >
                  Annuler
                </Button>
                <Button
                  onClick={sendNotification}
                  disabled={sendingNotification || (!notifyChannels.email && !notifyChannels.sms && !notifyChannels.whatsapp)}
                  className="flex-1 bg-blue-500 hover:bg-blue-600"
                >
                  {sendingNotification ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Envoi...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Demande d'Avis */}
      <ReviewRequestModal
        isOpen={!!showReviewModal}
        onClose={() => setShowReviewModal(null)}
        client={showReviewModal}
        token={token}
        onSuccess={() => toast.success('Avis demandé avec succès')}
      />
    </div>
  );
}
